import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useLocalization } from '~/framework';
import { getTeamFromClient, GetTeamErrorCode } from '~/pages/api/teams/getTeam.api';
import { useApiErrorMessageToast } from '~/pages/_shared';
import { Team } from '~/monolith-api';

const KEY = 'teams';

export function teamsKey(teamids: number[] | null): (string | number[] | null)[] {
   return [KEY, teamids];
}

export function useTeams(
   teamids: number[] | null,
   options?: UseQueryOptions<Team[], any, Team[], (string | number[] | null)[]>
) {
   const { _js } = useLocalization();
   const { handleErrorOrRedirectToLogin } = useApiErrorMessageToast<GetTeamErrorCode>({
      'teams.team_not_found': _js('Failed to find a recent team entry'),
      default: _js('There was a problem retrieving a recent team.'),
   });

   const queryFunction = async () => {
      if (!teamids?.length) return Promise.resolve([]);

      return Promise.all(
         teamids.map(async teamid => {
            try {
               return await getTeamFromClient(teamid);
            } catch (error: any) {
               if (error.code === 'ERR_BAD_REQUEST') {
                  return null;
               }
               throw error;
            }
         })
      ).then(values => {
         return values.filter((value): value is Team => value !== null);
      });
   };

   return useQuery({
      ...options,
      queryFn: queryFunction,
      queryKey: teamsKey(teamids),
      onError: handleErrorOrRedirectToLogin,
   });
}
